import { type IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cva, type VariantProps } from "class-variance-authority";
import { clsx } from "clsx";
import { type PropsWithChildren } from "react";

const buttonStyles = cva(
  "flex h-32 w-full items-center justify-center gap-x-4 overflow-hidden rounded-3xl px-8 py-6 backdrop-blur-sm",
  {
    variants: {
      variant: {
        idle: "bg-kiosk-text-white/15 font-semibold text-kiosk-text-white",
        transparent:
          "border border-kiosk-text-white font-semibold text-kiosk-text-white"
      },
      size: {
        medium: "text-4xl",
        large: "text-5xl"
      }
    },
    defaultVariants: {
      variant: "idle",
      size: "large"
    }
  }
);

const iconStyles = cva("", {
  variants: {
    variant: {
      idle: "text-kiosk-text-white",
      transparent: "text-kiosk-text-white"
    }
  },
  defaultVariants: {
    variant: "idle"
  }
});

type ButtonProps = VariantProps<typeof buttonStyles> &
  PropsWithChildren<{
    onClick?: () => void;
    icon?: IconDefinition;
    className?: string;
    hidden?: boolean;
    type?: "button";
    disabled?: boolean;
  }>;

/**
 * Properties for the Button component.
 * @typedef {Object} ButtonProps
 * @property {() => void} onClick - Callback function to be invoked when the button is clicked.
 * @property {IconDefinition} [icon] - Optional FontAwesome icon to be displayed within the button.
 * @property {string} [className] - Optional additional class names to be applied to the button.
 * @property {"button" | "submit"} [type] - Optional type for the button.
 * @property {boolean} [disabled] - Optional disabled state for the button.
 * @property {boolean} [hidden] - Optional hidden state for the button.
 */
export const Button = ({
  children,
  onClick,
  variant,
  icon,
  size,
  className,
  hidden = false,
  disabled = false,
  type = "button"
}: ButtonProps) => {
  return (
    <button
      type={type === "button" ? "button" : "submit"}
      className={buttonStyles({
        variant,
        size,
        className: clsx(className, {
          hidden
        })
      })}
      onClick={onClick}
      disabled={disabled}
    >
      <div className="z-10 flex items-center justify-center gap-x-4">
        {icon ? (
          <FontAwesomeIcon
            icon={icon}
            fontSize={36}
            className={iconStyles({ variant })}
          />
        ) : null}
        {children}
      </div>
    </button>
  );
};
